import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useProject } from "../contexts/ProjectContext";
import { useAuth } from "../contexts/AuthContext";
import { useUsage } from "../hooks/useUsage";
import SubscriptionModal from "./SubscriptionModal";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Plus, FolderOpen, Settings, Feather, Trash2, Edit3, Search, User, LogOut, Bookmark } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import TruncatedDescription from "./TruncatedDescription";

const Dashboard = () => {
  const { projects, createProject, deleteProject, loading } = useProject();
  const { user, logout } = useAuth();
  const { usage, refreshUsage } = useUsage();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [createButtonState, setCreateButtonState] = useState("idle"); // idle, creating, created
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    
    const query = searchQuery.toLowerCase();
    return projects.filter(project => 
      project.name.toLowerCase().includes(query) ||
      (project.description && project.description.toLowerCase().includes(query))
    );
  }, [projects, searchQuery]);

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast({
        title: "Error",
        description: "Project name is required",
        variant: "destructive"
      });
      return;
    }

    // Check if user can create more projects
    if (!usage.can_create_project) {
      setShowSubscriptionModal(true);
      return;
    }

    setCreateButtonState("creating");
    try {
      await createProject(newProject);
      setNewProject({ name: "", description: "" });
      setCreateButtonState("created");
      
      // Refresh usage after successful project creation
      refreshUsage();
      
      toast({
        title: "Project created",
        description: "Your new project has been created successfully."
      });
      setIsCreateDialogOpen(false);
      
      // Reset button state after showing success
      setTimeout(() => {
        setCreateButtonState("idle");
      }, 2000);
    } catch (error) {
      console.error('Error creating project:', error);
      
      // Handle specific error cases
      if (error.response?.status === 402) {
        const errorDetail = error.response.data?.detail;
        if (typeof errorDetail === 'object' && errorDetail.subscription_required) {
          setShowSubscriptionModal(true);
        } else {
          toast({
            title: "Project Limit Reached",
            description: errorDetail?.message || errorDetail || "You've reached your project limit. Upgrade to continue.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Error",
          description: "Failed to create project. Please try again.",
          variant: "destructive"
        });
      }
      
      setCreateButtonState("idle");
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      try {
        await deleteProject(projectId);
        
        // Refresh usage after successful project deletion
        refreshUsage();
        
        toast({
          title: "Deleted",
          description: "Project deleted successfully"
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete project",
          variant: "destructive"
        });
      }
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Feather className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                ItsMyVibe
              </h1>
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Loading your projects...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Feather className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                ItsMyVibe
              </h1>
            </Link>
            <nav className="flex items-center space-x-2 md:space-x-4">
              {/* Usage Information */}
              {(usage.subscription_status === 'free' || usage.subscription_status === 'unlocked') && (
                <div className="hidden sm:flex items-center space-x-4 text-xs text-slate-600 bg-slate-100 px-3 py-1.5 rounded-lg">
                  <span>Projects: {usage.projects_count}/{usage.subscription_status === 'unlocked' ? '∞' : '10'}</span>
                  <span>AI: {usage.ai_improvements_used}/{usage.subscription_status === 'unlocked' ? '∞' : '200'}</span>
                </div>
              )}
              
              <Link to="/system-prompts" className="hidden md:block">
                <Button variant="ghost" size="sm">
                  <Edit3 className="w-4 h-4 mr-2" />
                  System Prompts
                </Button>
              </Link>
              <Link to="/footnotes" className="hidden md:block">
                <Button variant="ghost" size="sm">
                  <Bookmark className="w-4 h-4 mr-2" />
                  Footnotes
                </Button>
              </Link>
              <Link to="/settings" className="hidden sm:block">
                <Button variant="ghost" size="sm">
                  <Settings className="w-4 h-4 mr-2" />
                  <span className="hidden lg:inline">Settings</span>
                </Button>
              </Link>
              
              {/* Mobile menu for hidden items */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="sm">
                    <Settings className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/system-prompts" className="flex items-center">
                      <Edit3 className="w-4 h-4 mr-2" />
                      System Prompts
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/footnotes" className="flex items-center">
                      <Bookmark className="w-4 h-4 mr-2" />
                      Footnotes
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="sm:hidden">
                    <Link to="/settings" className="flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* User dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span className="hidden md:inline">{user?.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center space-x-2 p-2">
                    <User className="w-4 h-4" />
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.email}</p>
                      <p className="text-xs text-muted-foreground">Signed in</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">
            Craft Your Perfect Prompts
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Organize, improve, and manage your AI prompts with ease. 
            Create projects, draft prompts, and enhance them with AI assistance.
          </p>
        </div>

        {/* Search and Projects Grid */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <h3 className="text-2xl font-semibold text-slate-800">Your Projects</h3>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
              {/* Search Box */}
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full sm:w-64"
                />
              </div>
              <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
                setIsCreateDialogOpen(open);
                if (open) {
                  setCreateButtonState("idle"); // Reset button state when opening dialog
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white whitespace-nowrap">
                    <Plus className="w-4 h-4 mr-2" />
                    New Project
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Project</DialogTitle>
                    <DialogDescription>
                      Create a new project to organize your prompts by topic or use case.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Project Name</Label>
                      <Input
                        id="name"
                        placeholder="e.g. Content Creation"
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Textarea
                        id="description"
                        placeholder="Brief description of this project..."
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      />
                    </div>
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateProject}
                        disabled={createButtonState !== "idle"}
                        className={createButtonState === "created" ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {createButtonState === "creating" && "Creating..."}
                        {createButtonState === "created" && "Created!"}
                        {createButtonState === "idle" && "Create Project"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Search Results Info */}
          {searchQuery && (
            <div className="mb-4 text-sm text-slate-600">
              Found {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </div>
          )}

          {filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              {searchQuery ? (
                <>
                  <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-xl font-medium text-slate-500 mb-2">No projects found</h4>
                  <p className="text-slate-400 mb-6">No projects match your search "{searchQuery}"</p>
                  <Button onClick={() => setSearchQuery("")} variant="outline">
                    Clear Search
                  </Button>
                </>
              ) : (
                <>
                  <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-xl font-medium text-slate-500 mb-2">No projects yet</h4>
                  <p className="text-slate-400 mb-6">Create your first project to get started with organizing your prompts</p>
                  <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Project
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project) => (
                <Link key={project.id} to={`/project/${project.id}`} className="block">
                  <Card className="group hover:shadow-lg transition-all duration-200 border-slate-200 hover:border-violet-300 cursor-pointer h-full">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg font-semibold text-slate-800 group-hover:text-violet-600 transition-colors">
                            {project.name}
                          </CardTitle>
                          <TruncatedDescription description={project.description} />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteProject(project.id, project.name);
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                        <span>{project.prompt_count} prompts</span>
                        <span>Modified {formatDate(project.latest_activity || project.created_at)}</span>
                      </div>
                      <div className="flex items-center text-sm text-violet-600 group-hover:text-violet-700 transition-colors">
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Click to open project
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentUsage={usage}
      />
    </div>
  );
};

export default Dashboard;